import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { EChartsOption } from 'echarts';
import { NgxEchartsDirective } from 'ngx-echarts';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { ImageService } from '@/app/services/image/image.service';
import { ThemeService } from '@/app/services/theme/theme.service';
import { StatsPeriodSelection } from '@/app/pages/admin-options/settings/molecules/admin-stats-aggregate/admin-stats-aggregate.types';
import { getKpiTrend } from '@/app/pages/admin-options/settings/molecules/admin-stats-dashboard/admin-stats-dashboard.helpers';
import { AdminStatsPeriodFilter } from '@/app/pages/admin-options/settings/molecules/admin-stats-period-filter/admin-stats-period-filter';
import { AdminCancellationsService } from '../../services/admin-cancellations.service';
import { CancellationDetailDrawer } from '../cancellation-detail-drawer/cancellation-detail-drawer';
import {
  OUTCOME_SERIES,
  RETURN_RAMP,
  returnRampColor,
} from '../../cancellations.palette';
import {
  buildFollowUpChart,
  buildReasonOutcomeChart,
  buildTimelineChart,
  formatMinorUnits,
  formatPercent,
  formatRangeLabel,
  formatRate,
  outcomeChipClass,
  outcomeLabel,
} from '../../cancellations.helpers';
import {
  CancellationInsights,
  CancellationMoney,
  OffboardingScope,
} from '../../cancellations.types';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error' | 'forbidden';

interface KpiCard {
  key: string;
  label: string;
  /** Pre-formatted, because half of these are rates and money rather than counts. */
  display: string;
  current: number;
  previous: number;
  /** Trend is meaningless for a rate with no denominator, and for money we show our own line. */
  showTrend: boolean;
  hint: string;
}

/**
 * Churn dashboard over the whole off-boarding funnel.
 *
 * Reads every outcome, not just cancellations: `kept` (talked out of leaving)
 * and `started` (survey answered, flow abandoned) are the cohorts that make the
 * rest actionable, and no endpoint before this exposed them.
 *
 * Charts bind `NgxEchartsDirective` directly rather than going through
 * `EchartGenericChartComponent`. That wrapper re-styles whatever option it is
 * given from a fixed set of variants, which would overwrite the validated
 * palette in `cancellations.palette.ts`.
 */
@Component({
  selector: 'admin-cancellations-dashboard',
  imports: [
    AdminStatsPeriodFilter,
    LoadingInlineComponent,
    LdgImgComponent,
    NgxEchartsDirective,
    CancellationDetailDrawer,
    DatePipe,
    UpperCasePipe,
  ],
  templateUrl: './cancellations-dashboard.html',
  styleUrl: './cancellations-dashboard.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancellationsDashboard implements OnDestroy {
  readonly insights = signal<CancellationInsights | null>(null);
  readonly loadingState = signal<LoadingState>('idle');
  readonly error = signal<string | null>(null);
  readonly scope = signal<OffboardingScope | 'all'>('all');
  readonly selectedReasonId = signal<string | null>(null);
  readonly openResponseId = signal<string | null>(null);

  readonly outcomeSeries = OUTCOME_SERIES;
  readonly returnRamp = RETURN_RAMP;
  readonly outcomeChipClass = outcomeChipClass;
  readonly outcomeLabel = outcomeLabel;
  readonly formatPercent = formatPercent;
  readonly getKpiTrend = getKpiTrend;
  readonly imageService = inject(ImageService);

  readonly reasonChart = signal<{ option: EChartsOption; hasData: boolean } | null>(null);
  readonly followUpChart = signal<{ option: EChartsOption; hasData: boolean } | null>(null);
  readonly timelineChart = signal<{ option: EChartsOption; hasData: boolean } | null>(null);

  /**
   * True while a refetch is in flight over an existing render.
   *
   * The body stays on screen at reduced opacity rather than collapsing to a
   * skeleton, so changing the period does not bounce the layout.
   */
  readonly isRefreshing = computed(
    () => this.loadingState() === 'loading' && this.insights() !== null,
  );

  readonly rangeLabel = computed(() => {
    const range = this.insights()?.range;
    return range ? formatRangeLabel(range.from, range.to) : '';
  });

  /** The busiest currency leads; anything else is listed under the card. */
  readonly primaryMoney = computed<CancellationMoney | null>(
    () => this.insights()?.money[0] ?? null,
  );

  readonly otherMoney = computed<CancellationMoney[]>(
    () => this.insights()?.money.slice(1) ?? [],
  );

  readonly kpiCards = computed<KpiCard[]>(() => {
    const data = this.insights();
    if (!data) return [];

    const { totals, previousTotals, rates, previousRates } = data;
    const money = this.primaryMoney();
    const previousMoney = data.previousMoney[0] ?? null;
    const currency = money?.currencyCode ?? null;

    // A reversal is a second event, not a retraction: a brand that cancelled and
    // then reactivated still cancelled, and still told us why.
    const cancelled = totals.cancelled + totals.reactivated;
    const previousCancelled = previousTotals.cancelled + previousTotals.reactivated;
    const paused = totals.paused + totals.pauseCancelled;
    const previousPaused = previousTotals.paused + previousTotals.pauseCancelled;

    return [
      {
        key: 'cancelled',
        label: 'Cancelled',
        display: cancelled.toLocaleString(),
        current: cancelled,
        previous: previousCancelled,
        showTrend: true,
        hint: totals.reactivated
          ? `${totals.reactivated} later reactivated`
          : 'Set to stop at period end',
      },
      {
        key: 'paused',
        label: 'Paused instead',
        display: paused.toLocaleString(),
        current: paused,
        previous: previousPaused,
        showTrend: true,
        hint: 'Took the pause offer',
      },
      {
        key: 'kept',
        label: 'Saved',
        display: totals.kept.toLocaleString(),
        current: totals.kept,
        previous: previousTotals.kept,
        showTrend: true,
        hint: 'Backed out and kept the subscription',
      },
      {
        key: 'started',
        label: 'Abandoned',
        display: totals.started.toLocaleString(),
        current: totals.started,
        previous: previousTotals.started,
        showTrend: true,
        hint: 'Answered the survey, never finished',
      },
      {
        key: 'saveRate',
        label: 'Save rate',
        display: formatRate(rates.saveRate),
        current: 0,
        previous: 0,
        // The shared trend line reads "vs 21 previous period" — fine for a count,
        // nonsense for a percentage. This card states its own comparison.
        showTrend: false,
        hint:
          previousRates.saveRate === null
            ? 'Of everyone who reached a decision'
            : `Of everyone who reached a decision · ${formatRate(previousRates.saveRate)} previous period`,
      },
      {
        key: 'churn',
        label: 'Revenue churned',
        display: formatMinorUnits(money?.monthlyChurnedMinorUnits ?? null, currency),
        current: money?.monthlyChurnedMinorUnits ?? 0,
        previous: previousMoney?.monthlyChurnedMinorUnits ?? 0,
        showTrend: false,
        hint: `${formatMinorUnits(money?.monthlySavedMinorUnits ?? null, currency)} a month saved`,
      },
    ];
  });

  readonly selectedFollowUp = computed(() => {
    const data = this.insights();
    if (!data) return null;
    const selected = this.selectedReasonId();
    return (
      data.followUps.find((group) => group.reasonId === selected) ??
      data.followUps[0] ??
      null
    );
  });

  readonly returnTotal = computed(() =>
    (this.insights()?.returnLikelihood ?? []).reduce(
      (sum, option) => sum + option.count,
      0,
    ),
  );

  private period: StatsPeriodSelection | null = null;
  private loadSubscription = new Subscription();

  private readonly cancellationsService = inject(AdminCancellationsService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);

  constructor() {
    // Charts rebuild when the data changes or the theme flips: the dark steps
    // are a selected palette, not an automatic inversion of the light one.
    effect(() => {
      const data = this.insights();
      const isDark = this.themeService.isDarkMode();

      if (!data) {
        this.reasonChart.set(null);
        this.followUpChart.set(null);
        this.timelineChart.set(null);
        return;
      }

      this.reasonChart.set(buildReasonOutcomeChart(data.reasons, isDark));
      this.followUpChart.set(buildFollowUpChart(this.selectedFollowUp(), isDark));
      this.timelineChart.set(
        buildTimelineChart(data.timeline, data.range.bucket, isDark),
      );
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription.unsubscribe();
  }

  onPeriodChange(selection: StatsPeriodSelection): void {
    this.period = selection;
    this.load();
  }

  setScope(scope: OffboardingScope | 'all'): void {
    if (this.scope() === scope) return;
    this.scope.set(scope);
    this.load();
  }

  getScopeButtonClass(scope: OffboardingScope | 'all'): string {
    return this.scope() === scope
      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100'
      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:bg-white/5';
  }

  /**
   * Clicking a bar retargets the follow-up panel to that reason.
   *
   * Typed structurally: `ngx-echarts` emits the instance from echarts' bundled
   * `shared` types, which TypeScript treats as a different class from the
   * top-level `ECharts` because both declare a private `_ssr`.
   */
  onReasonChartInit(chart: {
    on(event: string, handler: (params: unknown) => void): void;
  }): void {
    chart.on('click', (params: unknown) => {
      // The clicked bar is identified by its category label, which is the only
      // thing on screen — reasons are matched back by label, not by index,
      // because the bars are ranked and the order is data-dependent.
      const name = (params as { name?: unknown }).name;
      const reason = this.insights()?.reasons.find(
        (candidate) => candidate.label === name,
      );
      if (reason) {
        this.selectedReasonId.set(reason.id);
      }
    });
  }

  selectReason(reasonId: string): void {
    this.selectedReasonId.set(reasonId);
  }

  isReasonSelected(reasonId: string): boolean {
    return this.selectedFollowUp()?.reasonId === reasonId;
  }

  getReasonChipClass(reasonId: string): string {
    return this.isReasonSelected(reasonId)
      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100'
      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20';
  }

  returnSegmentColor(id: string): string {
    return returnRampColor(id, this.themeService.isDarkMode());
  }

  returnSegmentWidth(count: number): string {
    const total = this.returnTotal();
    return total > 0 ? `${(count / total) * 100}%` : '0%';
  }

  /** Every segment of the meter is a filter on the responses list. */
  openResponsesFor(returnLikelihood: string): void {
    void this.router.navigate(['/admin-options/cancellations/responses'], {
      queryParams: { ...this.rangeQueryParams(), returnLikelihood },
    });
  }

  openAllResponses(): void {
    void this.router.navigate(['/admin-options/cancellations/responses'], {
      queryParams: this.rangeQueryParams(),
    });
  }

  openResponse(id: string): void {
    this.openResponseId.set(id);
  }

  closeResponse(): void {
    this.openResponseId.set(null);
  }

  private rangeQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.period) {
      params['from'] = this.period.startDate;
      params['to'] = this.period.endDate;
    }
    if (this.scope() !== 'all') {
      params['scope'] = this.scope();
    }
    return params;
  }

  private load(): void {
    if (!this.period) return;

    this.loadSubscription.unsubscribe();
    this.loadSubscription = new Subscription();
    this.loadingState.set('loading');
    this.error.set(null);

    const scope = this.scope();

    this.loadSubscription.add(
      this.cancellationsService
        .getInsights({
          from: this.period.startDate,
          to: this.period.endDate,
          ...(scope === 'all' ? {} : { scope }),
        })
        .subscribe({
          next: (insights) => {
            this.insights.set(insights);
            if (!this.selectedReasonId()) {
              this.selectedReasonId.set(insights.followUps[0]?.reasonId ?? null);
            }
            this.loadingState.set('loaded');
          },
          error: (requestError: HttpErrorResponse) => {
            // The tab is visible to every admin but the API is GodMode-only, so
            // a 403 is an expected answer here, not a fault worth a red banner.
            if (requestError.status === 403) {
              this.loadingState.set('forbidden');
              return;
            }
            this.error.set(
              requestError.error?.message ?? 'Could not load cancellation insights',
            );
            this.loadingState.set('error');
          },
        }),
    );
  }
}
